import type { Order } from '~api/tos';

import { Card, ListLayout } from '@ingrid/luterkort-react';
import { CarrierName, ShippingLogo } from '@ingrid/react-shipping-logo';

import { Amount } from '~src/components/Amount/Amount';
import { LocalizedDate } from '~src/i18n/localized-date';
import { useTranslation } from '~src/i18n/use-translation';

type Props = Pick<Order, 'metadata'>;

export function CarrierAndTransportProduct({ metadata }: Props) {
  const { t } = useTranslation();

  if (!metadata?.session?.method) {
    return;
  }

  return (
    <Card.Wrapper>
      <Card.Header>{t('common.carrierProduct')}</Card.Header>

      <Card.Content style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ListLayout.SubRow>
          <ShippingLogo method={metadata.session.method} width={40} />
          <CarrierName method={metadata.session.method} />
        </ListLayout.SubRow>

        <ListLayout.Column
          as="dl"
          css={{
            borderBottomWidth: '0px',
            borderLeftWidth: '0px',
            borderRightWidth: '0px',
            borderStyle: 'solid',
            borderTopColor: 'var(--grey-e9)',
            borderTopWidth: '1px',
            paddingTop: '16px',
          }}
        >
          {metadata.session.shipping?.category_name && (
            <ListLayout.Stack as="div">
              <ListLayout.SecondaryText as="dt">{t('transportOrders.details.categoryName')}</ListLayout.SecondaryText>
              <ListLayout.PrimaryText as="dd">{metadata.session.shipping?.category_name}</ListLayout.PrimaryText>
            </ListLayout.Stack>
          )}
          {metadata.session.delivery_type && (
            <ListLayout.Stack as="div">
              <ListLayout.SecondaryText as="dt">{t('transportOrders.details.deliveryType')}</ListLayout.SecondaryText>
              <ListLayout.PrimaryText as="dd" style={{ textTransform: 'capitalize' }}>
                {metadata.session.delivery_type.toLowerCase()}
              </ListLayout.PrimaryText>
            </ListLayout.Stack>
          )}
          {metadata.session.pricing?.shipping_price && metadata.session.pricing?.currency && (
            <ListLayout.Stack as="div">
              <ListLayout.SecondaryText as="dt">{t('common.price')}</ListLayout.SecondaryText>
              <ListLayout.PrimaryText as="dd">
                <Amount currency={metadata.session.pricing.currency} value={metadata.session.pricing.shipping_price} />
              </ListLayout.PrimaryText>
            </ListLayout.Stack>
          )}

          {metadata.session.shipping?.time_slot && (
            <div style={{ display: 'flex' }}>
              {metadata.session.shipping?.time_slot?.start && (
                <ListLayout.Stack as="div" style={{ flex: '1 1 0%', minWidth: 'fit-content' }}>
                  <ListLayout.SecondaryText as="dt">
                    {t('transportOrders.details.startDeliveryTime')}
                  </ListLayout.SecondaryText>
                  <ListLayout.PrimaryText as="dd">
                    <LocalizedDate dateTime={metadata.session.shipping.time_slot.start} short time />
                  </ListLayout.PrimaryText>
                </ListLayout.Stack>
              )}

              {metadata.session.shipping?.time_slot?.end && (
                <ListLayout.Stack as="div" style={{ flex: '1 1 0%', minWidth: 'fit-content' }}>
                  <ListLayout.SecondaryText as="dt">
                    {t('transportOrders.details.endDeliveryTime')}
                  </ListLayout.SecondaryText>
                  <ListLayout.PrimaryText as="dd">
                    <LocalizedDate dateTime={metadata.session.shipping.time_slot.end} short time />
                  </ListLayout.PrimaryText>
                </ListLayout.Stack>
              )}
            </div>
          )}
        </ListLayout.Column>
      </Card.Content>
    </Card.Wrapper>
  );
}
